import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../../stores/appRevenueHistoryStore';
import { usePriceAlertsStore } from '../../../../stores/appPriceAlertsStore';
import { useRestaurantStore } from '../../../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../../stores/appUserDefaultStore';
import { useTimeIntervalStore } from '../../../../stores/appTimeIntervalStore';
import {
  useLoadOrderHistory,
  useLoadRevenueHistory,
  useLoadPriceAlerts,
} from '../../../../utils/dataLoadingUtils';
import { eur } from '../../../../components/orderhistory/utils';
import { rangeFromTimeControls, previousRange } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { SectionCard, EmptyState, ChangeBadge, ReportLoading, ReportTable, ReportColumn, pctAbs, SplitBar, RankBadge } from './common';
import {
  computeOverviewReport,
  computePerRestaurantOverview,
  SnapshotMetric,
  SnapshotSide,
  SellingItemRow,
  TrackedDishRow,
  SpendingProductRow,
  PriceAlertRow,
  RestaurantBreakdownRow,
} from '../../../../utils/reports/overviewReport';

const OverviewReport: React.FC = () => {
  const { t } = useTranslation();

  // Data is scoped to the selected restaurant by these hooks.
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadPriceAlerts();

  const orders = useOrderHistory((s) => s.orderHistoryList);
  const isLoadingOrders = useOrderHistory((s) => s.isLoading);
  const revenue = useRevenueHistory((s) => s.revenueHistoryList);
  const isLoadingRevenue = useRevenueHistory((s) => s.isLoading);
  const priceAlerts = usePriceAlertsStore((s) => s.priceAlerts);
  const isLoadingAlerts = usePriceAlertsStore((s) => s.isLoading);
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id ?? '';
  const isAllRestaurants = !!selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const reportSettings = useRestaurantStore((s) => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const restaurants = useRestaurantStore((s) => s.restaurants);

  const view = useTimeIntervalStore((s) => s.view);
  const startDate = useTimeIntervalStore((s) => s.startDate);
  const endDate = useTimeIntervalStore((s) => s.endDate);

  const data = cachedReportValue(
    'overview.data',
    [orders, revenue, priceAlerts, reportSettings, view, startDate, endDate],
    () => {
      const range = rangeFromTimeControls(view, startDate, endDate);
      const previous = previousRange(range);
      return computeOverviewReport({ orders, revenue, priceAlerts, reportSettings, range, previous });
    }
  );

  // Holding-company view: per-restaurant snapshot, only in "all restaurants" mode.
  const perRestaurant = cachedReportValue(
    'overview.perRestaurant',
    [orders, revenue, restaurants, isAllRestaurants, view, startDate, endDate],
    () => {
      if (!isAllRestaurants) return [] as RestaurantBreakdownRow[];
      const range = rangeFromTimeControls(view, startDate, endDate);
      const names = Object.fromEntries(restaurants.map((r) => [r.id, r.name]));
      return computePerRestaurantOverview(orders, revenue, range, names);
    }
  );

  // While the report's stores are still loading and empty, show a spinner instead
  // of flashing empty sections.
  if (
    (isLoadingOrders && orders.length === 0) ||
    (isLoadingRevenue && revenue.length === 0) ||
    (isLoadingAlerts && priceAlerts.length === 0)
  ) {
    return <ReportLoading />;
  }

  const hideComparison = data.comparisonHidden;

  return (
    <div className="mt-4">
      {hideComparison && (
        <p className="text-xs text-muted dark:text-muted-dark mb-3">{t('reports.overview.comparisonHidden')}</p>
      )}

      {/* 1. Snapshot — 3 rows (revenue / costs / cost%) of 3 cards (food/drinks/total) */}
      <SnapshotSection snapshot={data.snapshot} hideComparison={hideComparison} />

      {/* 1b. Per-restaurant breakdown (holding-company view, all-restaurants only) */}
      {isAllRestaurants && (
        <SectionCard title={t('reports.overview.byRestaurant.title')} subtitle={t('reports.overview.byRestaurant.subtitle')}>
          {perRestaurant.length === 0 ? (
            <EmptyState message={t('reports.overview.byRestaurant.empty')} />
          ) : (
            <PerRestaurantTable rows={perRestaurant} />
          )}
        </SectionCard>
      )}

      {/* 2. Top 5 selling items */}
      <SectionCard title={t('reports.overview.topSelling.title')}>
        <SplitBar
          leftLabel={t('reports.overview.topSelling.food')}
          rightLabel={t('reports.overview.topSelling.drinks')}
          leftValue={data.snapshot.food.revenue.current}
          rightValue={data.snapshot.drinks.revenue.current}
          format={eur}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SellingTable label={t('reports.overview.topSelling.food')} rows={data.topSellingFood} hideComparison={hideComparison} />
          <SellingTable label={t('reports.overview.topSelling.drinks')} rows={data.topSellingDrinks} hideComparison={hideComparison} />
        </div>
      </SectionCard>

      {/* 3. Tracked dish performance (hidden when none configured) */}
      {reportSettings &&
        ((reportSettings.trackedDishes?.food?.length ?? 0) > 0 ||
          (reportSettings.trackedDishes?.drinks?.length ?? 0) > 0) && (
          <SectionCard title={t('reports.overview.trackedDishes.title')}>
            {data.trackedDishes.length === 0 ? (
              <EmptyState message={t('reports.overview.trackedDishes.empty')} />
            ) : (
              <TrackedDishesTable rows={data.trackedDishes} hideComparison={hideComparison} />
            )}
          </SectionCard>
        )}

      {/* 4. Highest spending products (incl-tax) */}
      <SectionCard title={t('reports.overview.highestSpending.title')} subtitle={t('reports.overview.highestSpending.subtitle')}>
        <SplitBar
          leftLabel={t('reports.overview.highestSpending.kitchen')}
          rightLabel={t('reports.overview.highestSpending.bar')}
          leftValue={data.snapshot.food.costs.current}
          rightValue={data.snapshot.drinks.costs.current}
          format={eur}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SpendingTable label={t('reports.overview.highestSpending.kitchen')} rows={data.highestSpendingKitchen} />
          <SpendingTable label={t('reports.overview.highestSpending.bar')} rows={data.highestSpendingBar} />
        </div>
      </SectionCard>

      {/* 5. Price alerts in period */}
      <SectionCard title={t('reports.overview.priceAlerts.title')}>
        {data.priceAlerts.length === 0 ? (
          <EmptyState message={t('reports.overview.priceAlerts.empty')} />
        ) : (
          <PriceAlertsTable rows={data.priceAlerts} />
        )}
      </SectionCard>
    </div>
  );
};

// --- snapshot -------------------------------------------------------------

/** One snapshot metric card (matches the weekly-email card: label + %change on
 *  top, big value below). */
const MetricCard: React.FC<{
  label: string;
  metric: SnapshotMetric;
  format: (n: number) => string;
  goodWhenUp: boolean;
  isPercentagePoints?: boolean;
  hideComparison: boolean;
}> = ({ label, metric, format, goodWhenUp, isPercentagePoints, hideComparison }) => (
  <div className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-4">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-muted dark:text-muted-dark">{label}</span>
      {isPercentagePoints ? (
        <ChangeBadge value={metric.change} goodWhenUp={goodWhenUp} suffix="pp" hidden={hideComparison} />
      ) : (
        <ChangeBadge value={metric.percentChange} goodWhenUp={goodWhenUp} hidden={hideComparison} />
      )}
    </div>
    <div className="text-2xl font-bold text-textPrimary dark:text-textPrimary-dark text-center mt-3">
      {format(metric.current)}
    </div>
  </div>
);

/** One row of 3 metric cards (food / drinks / total) under a heading. */
const MetricRow: React.FC<{
  heading: string;
  sides: { food: SnapshotMetric; drinks: SnapshotMetric; total: SnapshotMetric };
  format: (n: number) => string;
  goodWhenUp: boolean;
  isPercentagePoints?: boolean;
  hideComparison: boolean;
}> = ({ heading, sides, format, goodWhenUp, isPercentagePoints, hideComparison }) => {
  const { t } = useTranslation();
  const cards: Array<{ key: 'food' | 'drinks' | 'total'; metric: SnapshotMetric }> = [
    { key: 'food', metric: sides.food },
    { key: 'drinks', metric: sides.drinks },
    { key: 'total', metric: sides.total },
  ];
  return (
    <div className="mb-5 last:mb-0">
      <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">{heading}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map(({ key, metric }) => (
          <MetricCard
            key={key}
            label={t(`reports.overview.snapshot.${key}`)}
            metric={metric}
            format={format}
            goodWhenUp={goodWhenUp}
            isPercentagePoints={isPercentagePoints}
            hideComparison={hideComparison}
          />
        ))}
      </div>
    </div>
  );
};

const SnapshotSection: React.FC<{
  snapshot: ReturnType<typeof computeOverviewReport>['snapshot'];
  hideComparison: boolean;
}> = ({ snapshot, hideComparison }) => {
  const { t } = useTranslation();
  const sideOf = (pick: (s: SnapshotSide) => SnapshotMetric) => ({
    food: pick(snapshot.food),
    drinks: pick(snapshot.drinks),
    total: pick(snapshot.total),
  });

  return (
    <SectionCard title={t('reports.overview.snapshot.title')}>
      <MetricRow
        heading={t('reports.overview.snapshot.revenue')}
        sides={sideOf((s) => s.revenue)}
        format={eur}
        goodWhenUp
        hideComparison={hideComparison}
      />
      <MetricRow
        heading={t('reports.overview.snapshot.costs')}
        sides={sideOf((s) => s.costs)}
        format={eur}
        goodWhenUp={false}
        hideComparison={hideComparison}
      />
      <MetricRow
        heading={t('reports.overview.snapshot.inslag')}
        sides={sideOf((s) => s.inslag)}
        format={pctAbs}
        goodWhenUp={false}
        isPercentagePoints
        hideComparison={hideComparison}
      />
      <div className="mt-2 pt-3 border-t border-border dark:border-border-dark flex items-center justify-between">
        <span className="text-sm font-medium text-muted dark:text-muted-dark">{t('reports.overview.snapshot.nonFoodCosts')}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{eur(snapshot.nonFoodCosts.current)}</span>
          <ChangeBadge value={snapshot.nonFoodCosts.percentChange} goodWhenUp={false} hidden={hideComparison} />
        </div>
      </div>
    </SectionCard>
  );
};

// --- per-restaurant breakdown ---------------------------------------------

const PerRestaurantTable: React.FC<{ rows: RestaurantBreakdownRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const columns: ReportColumn<RestaurantBreakdownRow>[] = [
    { key: 'restaurant', header: t('reports.overview.byRestaurant.restaurant'), render: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'revenue', header: t('reports.overview.byRestaurant.revenue'), align: 'right', render: (r) => <span className="font-medium">{eur(r.revenue)}</span> },
    { key: 'costs', header: t('reports.overview.byRestaurant.costs'), align: 'right', render: (r) => eur(r.costs) },
    { key: 'inslag', header: t('reports.overview.byRestaurant.inslag'), align: 'right', render: (r) => pctAbs(r.inslag) },
  ];
  const totals = rows.reduce(
    (acc, r) => ({ revenue: acc.revenue + r.revenue, costs: acc.costs + r.costs }),
    { revenue: 0, costs: 0 }
  );
  const totalInslag = totals.revenue > 0 ? (totals.costs / totals.revenue) * 100 : 0;
  return (
    <ReportTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.restaurantId}
      footer={
        <tr className="border-t border-grey-200 dark:border-borderPrimary-dark font-semibold text-textPrimary dark:text-textPrimary-dark">
          <td className="px-3 py-2">{t('reports.overview.byRestaurant.total')}</td>
          <td className="px-3 py-2 text-right">{eur(totals.revenue)}</td>
          <td className="px-3 py-2 text-right">{eur(totals.costs)}</td>
          <td className="px-3 py-2 text-right">{pctAbs(totalInslag)}</td>
        </tr>
      }
    />
  );
};

// --- selling items --------------------------------------------------------

const SellingTable: React.FC<{ label: string; rows: SellingItemRow[]; hideComparison: boolean }> = ({
  label,
  rows,
  hideComparison,
}) => {
  const { t } = useTranslation();
  const columns: ReportColumn<SellingItemRow>[] = [
    { key: 'rank', header: '', render: (_r, i) => <RankBadge rank={i + 1} /> },
    { key: 'item', header: t('reports.overview.topSelling.item'), render: (r) => r.name },
    {
      key: 'qty',
      header: t('reports.overview.topSelling.quantity'),
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <span>{r.quantity}</span>
          <ChangeBadge value={r.quantityChangePercent} hidden={hideComparison} />
        </div>
      ),
    },
    {
      key: 'revenue',
      header: t('reports.overview.topSelling.revenue'),
      align: 'right',
      render: (r) => <span className="font-medium">{eur(r.revenue)}</span>,
    },
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">{label}</h3>
      {rows.length === 0 ? (
        <EmptyState message={t('reports.overview.topSelling.empty')} />
      ) : (
        <ReportTable columns={columns} rows={rows} rowKey={(r) => r.name} />
      )}
    </div>
  );
};

// --- tracked dishes -------------------------------------------------------

const TrackedDishesTable: React.FC<{ rows: TrackedDishRow[]; hideComparison: boolean }> = ({ rows, hideComparison }) => {
  const { t } = useTranslation();
  const columns: ReportColumn<TrackedDishRow>[] = [
    {
      key: 'dish',
      header: t('reports.overview.trackedDishes.dish'),
      render: (r) => (
        <>
          {r.name}
          <span className="ml-2 text-xs text-muted dark:text-muted-dark">{t(`reports.overview.snapshot.${r.category}`)}</span>
        </>
      ),
    },
    {
      key: 'qty',
      header: t('reports.overview.trackedDishes.quantity'),
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <span>{r.quantity}</span>
          <ChangeBadge value={r.quantityChange} hidden={hideComparison} />
        </div>
      ),
    },
    {
      key: 'revenue',
      header: t('reports.overview.trackedDishes.revenue'),
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <span className="font-medium">{eur(r.revenue)}</span>
          <ChangeBadge value={r.revenueChange} hidden={hideComparison} />
        </div>
      ),
    },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r) => `${r.category}-${r.name}`} />;
};

// --- highest spending -----------------------------------------------------

const SpendingTable: React.FC<{ label: string; rows: SpendingProductRow[] }> = ({ label, rows }) => {
  const { t } = useTranslation();
  const columns: ReportColumn<SpendingProductRow>[] = [
    { key: 'rank', header: '', render: (_r, i) => <RankBadge rank={i + 1} /> },
    {
      key: 'product',
      header: t('reports.overview.highestSpending.product'),
      render: (r) => (
        <>
          {r.name}
          <span className="block text-xs text-muted dark:text-muted-dark">{r.supplier}</span>
        </>
      ),
    },
    {
      key: 'qty',
      header: t('reports.overview.highestSpending.quantity'),
      align: 'right',
      render: (r) => r.formattedQuantity ?? r.quantity,
    },
    {
      key: 'spend',
      header: t('reports.overview.highestSpending.spend'),
      align: 'right',
      render: (r) => <span className="font-medium">{eur(r.spent)}</span>,
    },
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">{label}</h3>
      {rows.length === 0 ? (
        <EmptyState message={t('reports.overview.highestSpending.empty')} />
      ) : (
        <ReportTable columns={columns} rows={rows} rowKey={(r) => `${r.name}_${r.supplier}`} />
      )}
    </div>
  );
};

// --- price alerts ---------------------------------------------------------

const PriceAlertsTable: React.FC<{ rows: PriceAlertRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  const columns: ReportColumn<PriceAlertRow>[] = [
    { key: 'product', header: t('reports.overview.priceAlerts.product'), render: (r) => r.productName },
    { key: 'supplier', header: t('reports.overview.priceAlerts.supplier'), render: (r) => r.platform ?? '—' },
    { key: 'from', header: t('reports.overview.priceAlerts.from'), align: 'right', render: (r) => eur(r.fromPrice) },
    { key: 'to', header: t('reports.overview.priceAlerts.to'), align: 'right', render: (r) => eur(r.toPrice) },
    {
      key: 'change',
      header: t('reports.overview.priceAlerts.change'),
      align: 'right',
      render: (r) => <ChangeBadge value={r.changePercent} goodWhenUp={false} />,
    },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r, i) => `${r.productName}-${i}`} />;
};

export default OverviewReport;
